import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import RichText from "@/components/RichText";

// See TextBlock.jsx for context — same trick for legacy plain-text vs HTML.
function isProbablyHtml(str) {
  return typeof str === "string" && /<\w+[\s>]/i.test(str);
}

/**
 * Two-column section: image on one side, title/body/feature-list/CTAs on the other.
 * `image_side` controls which side the image lands on. The section background
 * alternates via the parent `index` prop.
 */
export default function TwoColumnBlock({ data, index = 0 }) {
  const {
    title,
    body,
    features = [],
    image,
    image_side = "right",
    primary_label,
    primary_href,
    secondary_label,
    secondary_href,
  } = data || {};

  const imageLeft = image_side === "left";
  const striped = index % 2 === 0 ? "bg-slate-50/50" : "bg-white";

  return (
    <section className={`py-16 lg:py-24 ${striped}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
        >
          <div className={imageLeft ? "lg:order-2" : ""}>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h2>
            {body && (
              isProbablyHtml(body) ? (
                <RichText
                  html={body}
                  className="prose prose-slate max-w-none prose-p:text-slate-500 prose-a:text-sky-600 prose-a:no-underline hover:prose-a:underline mt-3 text-base"
                />
              ) : (
                <p className="mt-3 text-base text-slate-500 leading-relaxed whitespace-pre-line">
                  {body}
                </p>
              )
            )}
            {features.length > 0 && (
              <ul className="mt-6 space-y-3">
                {features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-sky-500 shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{f}</span>
                  </li>
                ))}
              </ul>
            )}
            {(primary_label || secondary_label) && (
              <div className="mt-8 flex flex-wrap items-center gap-3">
                {primary_label && (
                  <Link to={primary_href || "/contact"}>
                    <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-6 text-sm font-semibold">
                      {primary_label} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                )}
                {secondary_label && (
                  <Link to={secondary_href || "/contact"}>
                    <Button
                      variant="outline"
                      className="border-sky-200 text-sky-700 hover:bg-sky-50 hover:text-sky-700 rounded-full px-6 text-sm font-semibold"
                    >
                      {secondary_label} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </div>

          {image && (
            <div className={`${imageLeft ? "lg:order-1" : ""} rounded-xl overflow-hidden shadow-lg border border-slate-200`}>
              <img
                src={image}
                alt={title || ""}
                className="w-full h-64 lg:h-80 object-cover"
                loading="lazy"
              />
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
